import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import { api } from "@/api/client";
import {
  queryKeys,
  ZOZNAM_GC_MS,
  type KategoriaFilter,
  type ZakazkyListFilters,
} from "@/api/queryKeys";
import type {
  DataResponse,
  ListResponse,
  Pripojenie,
  PripojitRequest,
  ZakazkaCreate,
  ZakazkaDetail,
  ZakazkaListItem,
  ZakazkaUpdate,
} from "@/api/schemas";

/**
 * Načítanie zoznamu zákaziek (filter, sort, pagination).
 *
 * Pri zmene strany, filtra či hľadania ostanú staré riadky, kým prídu nové
 * (`keepPreviousData`, KOLO 225) — dovtedy zoznam zakaždým blikol na
 * prázdnu kostru. Stránka ich počas načítania stlmí (`isPlaceholderData`).
 */
export function useZakazkyList(
  filters: ZakazkyListFilters = {},
): UseQueryResult<ListResponse<ZakazkaListItem>> {
  return useQuery({
    queryKey: queryKeys.zakazky.list(filters),
    queryFn: async () => {
      const { data } = await api.get<ListResponse<ZakazkaListItem>>(
        "/zakazky",
        { params: filters },
      );
      return data;
    },
    placeholderData: keepPreviousData,
    gcTime: ZOZNAM_GC_MS,
  });
}

/** Počty zákaziek podľa stavu — pre dlaždice v prehľade (v rámci kategórie). */
export function useZakazkyStavPocty(
  filtre: KategoriaFilter = {},
): UseQueryResult<DataResponse<Record<string, number>>> {
  return useQuery({
    queryKey: queryKeys.zakazky.stavPocty(filtre),
    queryFn: async () => {
      const { data } = await api.get<DataResponse<Record<string, number>>>(
        "/zakazky/stav-pocty",
        { params: filtre },
      );
      return data;
    },
  });
}

/** Detail zákazky s celou hierarchiou (varianty + sloty + zostavy + dielce). */
export function useZakazkaDetail(
  id: string | undefined,
): UseQueryResult<DataResponse<ZakazkaDetail>> {
  return useQuery({
    queryKey: queryKeys.zakazky.detail(id ?? ""),
    queryFn: async () => {
      const { data } = await api.get<DataResponse<ZakazkaDetail>>(
        `/zakazky/${id}`,
      );
      return data;
    },
    enabled: !!id,
    // Živý refresh — zmeny kolegu sa objavia bez F5 (interval + návrat na tab).
    // Scoped len na detail zákazky, aby global auto-save inde nebol rušený.
    refetchInterval: 20_000,
    refetchOnWindowFocus: true,
  });
}

/** Vytvor zákazku (default varianta A). */
export function useCreateZakazka(): UseMutationResult<
  DataResponse<ZakazkaDetail>,
  Error,
  ZakazkaCreate
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: ZakazkaCreate) => {
      const { data } = await api.post<DataResponse<ZakazkaDetail>>(
        "/zakazky",
        payload,
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.zakazky.lists() });
    },
  });
}

/** PATCH zákazky (nazov, stav, klient, …). */
export function useUpdateZakazka(): UseMutationResult<
  DataResponse<ZakazkaDetail>,
  Error,
  { id: string; data: ZakazkaUpdate }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }) => {
      const response = await api.patch<DataResponse<ZakazkaDetail>>(
        `/zakazky/${id}`,
        data,
      );
      return response.data;
    },
    onSuccess: (odpoved, variables) => {
      // Odpoveď PATCH je celý detail — zapíše sa hneď. Do KOLA 219 sa len
      // invalidovalo: okno sa zavrelo a starý názov ostal, kým nedobehlo
      // nové GET (zoznam ráta cenu každej zákazky, trvá to). User: *„zmenilo
      // sa, až keď som refreshol stránku"*.
      qc.setQueryData(queryKeys.zakazky.detail(variables.id), odpoved);
      qc.setQueriesData<ListResponse<ZakazkaListItem>>(
        {
          queryKey: queryKeys.zakazky.lists(),
          // Pod `lists()` sú aj počty stavov a kategórií — tie nie
          predicate: (q) =>
            typeof q.queryKey[2] === "object" && q.queryKey[2] !== null,
        },
        (stare) => prepisPolozku(stare, odpoved.data),
      );
      qc.invalidateQueries({ queryKey: queryKeys.zakazky.detail(variables.id) });
      qc.invalidateQueries({ queryKey: queryKeys.zakazky.lists() });
    },
  });
}

/** Polia, ktoré má riadok zoznamu aj detail — cena a počty príde z GET. */
const POLIA_ZOZNAMU = [
  "cislo_zakazky",
  "vypocet_cislo",
  "cislo_z",
  "nazov",
  "stav",
  "fakturovana",
  "kategoria_id",
  "updated_at",
] as const satisfies readonly (keyof ZakazkaListItem & keyof ZakazkaDetail)[];

function prepisPolozku(
  stare: ListResponse<ZakazkaListItem> | undefined,
  detail: ZakazkaDetail,
): ListResponse<ZakazkaListItem> | undefined {
  if (!stare || !Array.isArray(stare.data)) return stare;
  if (!stare.data.some((z) => z.id === detail.id)) return stare;
  const zmeny = Object.fromEntries(
    POLIA_ZOZNAMU.filter((k) => k in detail).map((k) => [k, detail[k]]),
  );
  return {
    ...stare,
    data: stare.data.map((z) => (z.id === detail.id ? { ...z, ...zmeny } : z)),
  };
}

/** Tvrdé zmazanie zákazky (nenávratné — vrátane variant a dielcov). */
export function useDeleteZakazka(): UseMutationResult<void, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/zakazky/${id}`);
    },
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: queryKeys.zakazky.detail(id) });
      qc.invalidateQueries({ queryKey: queryKeys.zakazky.lists() });
    },
  });
}

/** Archivuj zákazku (presun do archívu, dá sa obnoviť). */
export function useArchiveZakazka(): UseMutationResult<
  DataResponse<ZakazkaDetail>,
  Error,
  string
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.post<DataResponse<ZakazkaDetail>>(
        `/zakazky/${id}/archivovat`,
      );
      return data;
    },
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: queryKeys.zakazky.detail(id) });
      qc.invalidateQueries({ queryKey: queryKeys.zakazky.lists() });
    },
  });
}

/** Obnov zákazku z archívu späť medzi zákazky. */
export function useRestoreZakazka(): UseMutationResult<
  DataResponse<ZakazkaDetail>,
  Error,
  string
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.post<DataResponse<ZakazkaDetail>>(
        `/zakazky/${id}/obnovit`,
      );
      return data;
    },
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: queryKeys.zakazky.detail(id) });
      qc.invalidateQueries({ queryKey: queryKeys.zakazky.lists() });
    },
  });
}

import type { CenaHistoria } from "@/api/schemas";

/**
 * História cien zákazky — snímky pri prechodoch stavu (KOLO 62c).
 *
 * Read-only. Snímky sa zapisujú na backende v momente prechodu, lebo ceny
 * sú on-demand a historický súčet sa **nedá dopočítať** — cenník aj dielce
 * sa medzitým zmenili.
 */
export function useCenaHistoria(
  zakazkaId: string | undefined,
  enabled = true,
): UseQueryResult<DataResponse<CenaHistoria>> {
  return useQuery({
    queryKey: queryKeys.zakazky.cenaHistoria(zakazkaId ?? ""),
    queryFn: async () => {
      const { data } = await api.get<DataResponse<CenaHistoria>>(
        `/zakazky/${zakazkaId}/cena-historia`,
      );
      return data;
    },
    enabled: !!zakazkaId && enabled,
  });
}

/**
 * Pripojenie výroby k výpočtu (KOLO 208) a spojenie dvoch výrob (KOLO 209)
 * — `nahlad` je ten istý beh na serveri, ktorý sa nezapíše; okno ním
 * ukáže, čo sa stane. Pri zmene voľby (názov, hlavné číslo, mená častí)
 * ostane predošlý náhľad, kým nepríde nový.
 */
export function usePripojitNahlad(
  cielId: string | null,
  vyroba: PripojitRequest | null,
): UseQueryResult<DataResponse<Pripojenie>> {
  return useQuery({
    queryKey: [...queryKeys.zakazky.all, "pripojit-nahlad", cielId, vyroba],
    queryFn: async () => {
      const { data } = await api.post<DataResponse<Pripojenie>>(
        `/zakazky/${cielId}/pripojit/nahlad`,
        vyroba,
      );
      return data;
    },
    enabled: !!cielId && !!vyroba,
    placeholderData: (predosly) => predosly,
    retry: false,
  });
}

export function usePripojitVyrobu(): UseMutationResult<
  DataResponse<Pripojenie>,
  Error,
  { cielId: string } & PripojitRequest
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ cielId, ...telo }) => {
      const { data } = await api.post<DataResponse<Pripojenie>>(
        `/zakazky/${cielId}/pripojit`,
        telo,
      );
      return data;
    },
    onSuccess: () => {
      // Zmizne jedna zákazka, druhá dostane číslo, stav aj výrobu
      void qc.invalidateQueries({ queryKey: queryKeys.zakazky.all });
    },
  });
}
